import { useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { alpha, Button, Stack, Typography, TextField } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

import { FormInput, FormMediaUpload, FormSelector, Info, Spinner } from 'components';
import { ApiUser, UpdateClinicUserRequest, UpdateUserRequest } from 'types';
import { IApiRequests, useApiRequests, useUnsavedChangesPrompt } from 'hooks';
import { useAppDispatch, useAppSelector } from 'store';
import { getUser, selectUser } from 'store/user';
import { getSuffixesOptionsForUser, getTitlesOptionsForUser } from 'utils';
import { isHipaaLink } from '../../../../config/productProfile';

import styles from './MyProfileForm.module.scss';

type MyProfileFormType = {
  clinicUser: UpdateClinicUserRequest;
  user: UpdateUserRequest;
};

type MyProfileFormProps = {
  onDirtyChange?(dirty: boolean): void;
};

export function MyProfileForm({ onDirtyChange }: MyProfileFormProps) {
  const { t, i18n } = useTranslation('settings');
  const isRtl = i18n.dir() === 'rtl';
  const { enqueueSnackbar } = useSnackbar();
  const { updateProfilePicture, updateUser, updateClinicUser }: IApiRequests = useApiRequests();
  const user: ApiUser = useAppSelector(selectUser)!;
  const dispatch = useAppDispatch();

  const schema = Yup.object().shape({
    clinicUser: Yup.object().shape({
      suffix: Yup.string().nullable(),
    }),
    user: Yup.object().shape({
      firstName: Yup.string().max(50, t('myProfileForm.maxLength50')).required(t('myProfileForm.firstNameRequired')),
      lastName: Yup.string().max(50, t('myProfileForm.maxLength50')).required(t('myProfileForm.lastNameRequired')),
      title: Yup.string().nullable(),
      speciality: Yup.string().max(25, t('myProfileForm.maxLength25')).nullable(),
    }),
  });

  const formDefaultValues: MyProfileFormType = useMemo(
    () => ({
      user: {
        firstName: user.firstName,
        lastName: user.lastName,
        speciality: user.speciality ?? '',
        title: user.title,
      },
      clinicUser: {
        suffix: user.clinicUser.suffix,
      },
    }),
    [user],
  );

  const form = useForm<MyProfileFormType>({
    resolver: yupResolver(schema),
    defaultValues: formDefaultValues,
    mode: 'all',
  });

  const [profilePicture, setProfilePicture] = useState<File | null>(null);
  const [profilePictureChanged, setProfilePictureChanged] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const supportEmail = isHipaaLink ? 'support@hipaalink.net' : 'support@via3corp.com';
  const profileSelectMenuProps = useMemo(
    () => ({
      PaperProps: {
        sx: (theme: Theme) => ({
          '& .MuiMenuItem-root:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
          },
          '& .MuiMenuItem-root.Mui-selected': {
            backgroundColor: alpha(theme.palette.primary.main, 0.14),
          },
          '& .MuiMenuItem-root.Mui-selected:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.2),
          },
        }),
      },
    }),
    [],
  );

  const mailtoHref = `mailto:${supportEmail}?subject=Change%20email%20address%20request`;
  const hasUnsavedChanges = form.formState.isDirty || profilePictureChanged;

  useUnsavedChangesPrompt(hasUnsavedChanges, t('unsavedChangesPrompt'));

  useEffect(() => {
    onDirtyChange?.(hasUnsavedChanges);
  }, [hasUnsavedChanges, onDirtyChange]);

  useEffect(() => {
    return () => {
      onDirtyChange?.(false);
    };
  }, [onDirtyChange]);

  useEffect(() => {
    form.reset(formDefaultValues);
  }, [formDefaultValues]);

  const onSubmit = (): Promise<void> => {
    setLoading(true);

    const formValues: MyProfileFormType = form.getValues();

    const updateImagePromise = (): Promise<void> => {
      if (!profilePictureChanged) {
        return Promise.resolve();
      }

      return updateProfilePicture(profilePicture).catch((error) =>
        Promise.reject(`Unable to update profile picture: ${error}`),
      );
    };

    const updateClinicUserPromise = (): Promise<any> => {
      return updateClinicUser(formValues.clinicUser).catch((error: any) => {
        return Promise.reject(`Update clinic user failed: ${error.message}`);
      });
    };

    const updateUserPromise = (): Promise<any> => {
      return updateUser(formValues.user).catch((error: any) => {
        return Promise.reject(`Update user failed: ${error.message}`);
      });
    };

    return updateImagePromise()
      .then(() => updateClinicUserPromise())
      .then(() => updateUserPromise())
      .then(() => dispatch(getUser()).unwrap())
      .then(() => Promise.resolve())
      .finally(() => {
        setLoading(false);
      });
  };

  const handleSubmit = async (): Promise<void> => {
    if (!form.formState.isValid) {
      return;
    }

    await onSubmit()
      .then(() => {
        form.reset();
        setProfilePictureChanged(false);
        onDirtyChange?.(false);
      })
      .catch((error) => {
        enqueueSnackbar(error, { variant: 'error' });
      });
  };

  const handleProfilePictureChange = (picture: File | null) => {
    setProfilePicture(picture);
    setProfilePictureChanged(true);
  };

  return loading ? (
    <Spinner small />
  ) : (
    <div className={styles.form}>
      <Stack className={styles.row}>
        <Stack direction='row' alignItems='flex-end'>
          <Typography component='p' variant='body2'>
            {t('myProfileForm.profilePicture')}
          </Typography>
        </Stack>
        <FormMediaUpload
          onInputChange={handleProfilePictureChange}
          onRemove={() => handleProfilePictureChange(null)}
          title={t('myProfileForm.profilePicture')}
          src={user?.linkToProfilePicture ?? ''}
        />
      </Stack>

      <Stack className={styles.row}>
        <Stack direction='row' alignItems='center'>
          <Typography component='p' variant='body2'>
            {t('myProfileForm.title')}
          </Typography>
        </Stack>
        <FormSelector
          variant='outlined'
          name='user.title'
          control={form.control}
          options={getTitlesOptionsForUser()}
          MenuProps={profileSelectMenuProps}
        />
      </Stack>

      <Stack className={styles.row}>
        <Stack direction='row' alignItems='flex-end'>
          <Typography component='p' variant='body2'>
            {t('myProfileForm.firstName')} <span className={styles.asterisk}>*</span>
          </Typography>
        </Stack>
        <FormInput name='user.firstName' control={form.control} />
      </Stack>

      <Stack className={styles.row}>
        <Stack direction='row' alignItems='flex-end'>
          <Typography component='p' variant='body2'>
            {t('myProfileForm.lastName')} <span className={styles.asterisk}>*</span>
          </Typography>
        </Stack>
        <FormInput name='user.lastName' control={form.control} />
      </Stack>

      {isHipaaLink && (
        <Stack className={styles.row}>
          <Stack direction='row' alignItems='center'>
            <Typography component='p' variant='body2'>
              {t('myProfileForm.suffix')}
            </Typography>
          </Stack>
          <FormSelector
            variant='outlined'
            name='clinicUser.suffix'
            control={form.control}
            options={getSuffixesOptionsForUser()}
            MenuProps={profileSelectMenuProps}
          />
        </Stack>
      )}

      <Stack className={styles.row}>
        <Stack direction='row' alignItems='center' className={styles.emailLabelRow}>
          <span className={styles.emailLabelContent}>
            {isRtl ? (
              <>
                <Info
                  children={
                    <div>
                      {t('myProfileForm.contactUs')}&nbsp;
                      <a className={styles.emailTooltipHref} href={mailtoHref}>
                        {t('myProfileForm.here')}
                      </a>
                      &nbsp;{t('myProfileForm.toChangeYourEmail')}
                    </div>
                  }
                />
                <Typography component='p' variant='body2'>
                  {t('myProfileForm.email')}
                </Typography>
              </>
            ) : (
              <>
                <Typography component='p' variant='body2'>
                  {t('myProfileForm.email')}
                </Typography>
                <Info
                  children={
                    <div>
                      {t('myProfileForm.contactUs')}&nbsp;
                      <a className={styles.emailTooltipHref} href={mailtoHref}>
                        {t('myProfileForm.here')}
                      </a>
                      &nbsp;{t('myProfileForm.toChangeYourEmail')}
                    </div>
                  }
                />
              </>
            )}
          </span>
        </Stack>
        <TextField size='small' className={styles.email_textField} value={user.email} disabled />
      </Stack>

      <Stack className={styles.row}>
        <Stack direction='row' alignItems='flex-end'>
          <Typography component='p' variant='body2'>
            {isHipaaLink ? t('myProfileForm.specialtySpecialties') : t('myProfileForm.positionRole')}
          </Typography>
        </Stack>

        <FormInput name='user.speciality' control={form.control} />
      </Stack>

      <Stack className={styles.row} alignItems='flex-end'>
        <Button
          variant='contained'
          onClick={handleSubmit}
          disabled={(!form.formState.isValid || !form.formState.isDirty) && !profilePictureChanged}
          className={styles.nextButton}
          type='submit'
        >
          {t('myProfileForm.save')}
        </Button>
      </Stack>
    </div>
  );
}
